import { useEffect, useState } from "react";

export const HERO_SLIDE_INTERVAL = 8000;
const motionPreference = () => typeof window.matchMedia === "function"
	? window.matchMedia("(prefers-reduced-motion: reduce)")
	: null;

export default function useHeroShowcase(count, containerRef) {
	const [active, setActive] = useState(0);
	const [direction, setDirection] = useState("forward");
	const [reducedMotion, setReducedMotion] = useState(() => Boolean(motionPreference()?.matches));
	const [playing, setPlaying] = useState(() => !motionPreference()?.matches);
	const [hovered, setHovered] = useState(false);
	const [visible, setVisible] = useState(true);
	const [pageVisible, setPageVisible] = useState(() => !document.hidden);
	const rotating = playing && !hovered && visible && pageVisible && !reducedMotion;

	useEffect(() => {
		const media = motionPreference();
		if (!media) return undefined;
		const update = () => {
			setReducedMotion(media.matches);
			if (media.matches) setPlaying(false);
		};
		if (media.addEventListener) media.addEventListener("change", update);
		else media.addListener(update);
		return () => {
			if (media.removeEventListener) media.removeEventListener("change", update);
			else media.removeListener(update);
		};
	}, []);

	useEffect(() => {
		const update = () => setPageVisible(!document.hidden);
		document.addEventListener("visibilitychange", update);
		return () => document.removeEventListener("visibilitychange", update);
	}, []);

	useEffect(() => {
		if (typeof IntersectionObserver === "undefined" || !containerRef.current) return undefined;
		const observer = new IntersectionObserver(([entry]) => {
			setVisible(entry.isIntersecting && entry.intersectionRatio >= .25);
		}, { threshold: .25 });
		observer.observe(containerRef.current);
		return () => observer.disconnect();
	}, [containerRef]);

	useEffect(() => {
		if (!rotating) return undefined;
		const timer = setTimeout(() => { setDirection("forward"); setActive((current) => (current + 1) % count); }, HERO_SLIDE_INTERVAL);
		return () => clearTimeout(timer);
	}, [active, count, rotating]);

	const select = (index) => {
		setPlaying(false);
		setDirection(index < active ? "backward" : "forward");
		setActive((index + count) % count);
	};

	return {
		active, direction, select, reducedMotion, playing, rotating, setHovered,
		pause: () => setPlaying(false),
		togglePlaying: () => setPlaying((current) => !current),
	};
}
